import type { QueryClient } from "@tanstack/react-query";

import type {
  ReviewCounts,
  ReviewItemStatus,
  ReviewMarkTarget,
} from "./medicalReview.types";

export const REVIEW_ITEMS_DATASET = "medical_review_items";
export const REVIEW_MARKS_DATASET = "medical_review_marks";

/** The marks endpoint refuses a batch larger than this. */
export const MAX_BULK_MARKS = 500;

const REVIEW_DATASETS = [REVIEW_ITEMS_DATASET, REVIEW_MARKS_DATASET];

/**
 * Dataset rows, filter values and plots are all fetched through `useFetch`,
 * whose first query-key segment is a URL carrying `dataset_type`. Matching on
 * that refreshes every one of them without remounting the template, which
 * would throw away the reviewer's filters, paging and column layout.
 */
export function invalidateReviewDatasets(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const [endpoint] = query.queryKey;
      return (
        typeof endpoint === "string" &&
        REVIEW_DATASETS.some((dataset) => endpoint.includes(dataset))
      );
    },
  });
}

export function reviewRowId(row: Record<string, unknown>): number {
  return Number(row.id);
}

export function reviewRowText(
  row: Record<string, unknown>,
  field: string,
): string {
  const value = row[field];
  return value === null || value === undefined ? "" : String(value);
}

export function reviewRowStatus(
  row: Record<string, unknown>,
): ReviewItemStatus {
  const status = reviewRowText(row, "status");
  return status === "reviewed" || status === "re_review"
    ? status
    : "unreviewed";
}

/** Dataset booleans arrive as real booleans, or as text from a JSONB snapshot. */
export function reviewRowFlag(
  row: Record<string, unknown>,
  field: string,
): boolean {
  const value = row[field];
  if (typeof value === "boolean") return value;
  return ["true", "True", "1", "Yes"].includes(String(value ?? ""));
}

/**
 * Marking echoes back the hash of the content the reviewer saw, so a record
 * that moved cannot be signed for blind. Records whose hash is unknown are
 * reported rather than sent with a guess.
 */
export function toMarkTargets(hashById: Map<number, string>): {
  targets: ReviewMarkTarget[];
  unresolved: number[];
} {
  const targets: ReviewMarkTarget[] = [];
  const unresolved: number[] = [];
  for (const [itemId, materialHash] of hashById) {
    if (materialHash)
      targets.push({ item_id: itemId, material_hash: materialHash });
    else unresolved.push(itemId);
  }
  return { targets, unresolved };
}

/**
 * What "the queue moved" means for the new-data banner: any count changed, or
 * the scanner advanced its watermark.
 */
export function countsSignature(
  counts: ReviewCounts | undefined,
): string | null {
  if (!counts) return null;
  return JSON.stringify([
    counts.counts,
    counts.last_scan?.watermark_at ?? null,
  ]);
}

/**
 * Row order for the Context timeline and its table, read top to bottom as the
 * clinical story runs: the participant is screened, a lab comes back
 * off-range, an adverse event follows, a medication is started for it. Dosing
 * and deviations sit underneath as context on the treatment itself.
 *
 * Shared rather than duplicated because the table is read as the timeline's
 * key — the two disagreeing would make the panel harder to follow, not easier.
 */
export const REVIEW_DOMAIN_ORDER = [
  "eligibility",
  "labs",
  "adverse_events",
  "conmeds",
  "ip_administration",
  "protocol_deviations",
];

/** Sort position for a domain; anything unrecognised falls to the bottom. */
export function domainRank(domain: string): number {
  const index = REVIEW_DOMAIN_ORDER.indexOf(domain);
  return index === -1 ? REVIEW_DOMAIN_ORDER.length : index;
}

export function formatFileSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
